import * as React from 'react';
import { Builder } from '@builder.io/react';
import { iconNames, IconRotation, iconRotations } from '@/components/shared';

export interface ButtonComponentProps<TVariant, TSize> {
	readonly text?: string;
	readonly link?: string;
	readonly openInNewTab?: boolean;
	readonly variant?: TVariant;
	readonly size?: TSize;
	readonly ariaLabel?: string;
	readonly rotateIcon?: IconRotation;
}

const register = <
	TProps extends ButtonComponentProps<TVariants[number], TSizes[number]>,
	TVariants extends readonly string[],
	TSizes extends readonly string[],
>(
	Component: React.FC<TProps>,
	options: {
		readonly variants: TVariants;
		readonly sizes: TSizes;
		readonly defaultVariant?: TVariants[number];
		readonly defaultSize?: TSizes[number];
	},
): void => {
	Builder.registerComponent(Component, {
		name: 'Core:Button',
		override: true,
		inputs: [
			{
				name: 'text',
				type: 'string',
				defaultValue: 'Click me!',
				bubble: true,
			},
			{
				name: 'link',
				type: 'url',
				bubble: true,
			},
			{
				name: 'openInNewTab',
				type: 'boolean',
				defaultValue: false,
				friendlyName: 'Open link in new tab',
			},
			{
				name: 'variant',
				type: 'string',
				enum: Array.from(options.variants),
				defaultValue: options.defaultVariant,
			},
			{
				name: 'size',
				type: 'string',
				enum: Array.from(options.sizes),
				defaultValue: options.defaultSize,
			},
			{
				name: 'ariaLabel',
				type: 'string',
				friendlyName: 'ARIA label',
				helperText: 'Used by screen readers, e.g. if the button only contains an icon.',
			},
			{
				name: 'iconBefore',
				type: 'string',
				enum: Array.from(iconNames),
				advanced: true,
			},
			{
				name: 'iconAfter',
				type: 'string',
				enum: Array.from(iconNames),
				advanced: true,
			},
			{
				name: 'rotateIcon',
				type: 'string',
				enum: Array.from(iconRotations),
				advanced: true,
			},
		],
	});
};

export default register;
